import { useEffect, useState } from 'react';
import { getHealth } from '@/lib/api-client/health';
import type { HealthResponse } from '@/lib/api-client/health';
import { ApiError } from '@/lib/api-client/client';

type HealthState =
  | { status: 'loading' }
  | { status: 'success'; data: HealthResponse }
  | { status: 'error'; error: ApiError };

/**
 * Phase 0 deliberately uses a plain useState/useEffect hook rather than
 * React Query — there is exactly one read-only query in this whole phase.
 * React Query is introduced in Phase 5 once there's real server state worth
 * its caching/invalidation machinery. docs/ARCHITECTURE.md §1.5.
 */
export function useHealth(): HealthState {
  const [state, setState] = useState<HealthState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    getHealth()
      .then((data) => {
        if (!cancelled) setState({ status: 'success', data });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const apiError =
          error instanceof ApiError
            ? error
            : new ApiError('NETWORK_ERROR', 'Unexpected error contacting the API.');
        setState({ status: 'error', error: apiError });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
